import { useCallback } from 'react';
import { CanceledError, isAxiosError, isCancel } from 'axios';
import { isNetworkError } from '../../utils/isNetworkError';

const RETRY_COUNT = 3;
const ERRORS_THAT_SHOULD_NOT_BE_RETRIED = [400, 403, 404, 412, 500, 503];

/**
 * A React Query hook that provides a default retry policy for queries and mutations.
 * It specifies conditions under which a request should be retried based on the error type and failure count.
 *
 * @template TError The type of the error object passed to the retry function.
 * @returns A function `shouldRetry(failureCount, error)` that determines if a request should be retried.
 *          - Returns `false` for network errors (`ERR_NETWORK`) or specific HTTP status codes (400, 403, 404, 412, 500, 503).
 *          - Returns `true` if the `failureCount` is less than `RETRY_COUNT` (currently 3) for other errors, including 401 (handled separately).
 */
export function useDefaultRetryPolicy<TError>() {
  const shouldRetry = useCallback((failureCount: number, error: TError) => {
    // Retry network errors many times (machine wake-up scenario)
    if (isNetworkError(error)) {
      return failureCount < 10; // Retry up to 10 times for network errors
    }

    // Don't Retry on some 4xx errors, 5xx errors
    if (
      isAxiosError(error) &&
      error.response?.status &&
      ERRORS_THAT_SHOULD_NOT_BE_RETRIED.includes(error.response?.status)
    ) {
      return false;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }

    if (error instanceof CanceledError || isCancel(error)) {
      return false;
    }

    // 401 errors should be retried. They are handled by the error handler and if the handling
    // fails, the logic will redirect to authentication.
    return failureCount < RETRY_COUNT;
  }, []);

  return shouldRetry;
}
